import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminGetClients, adminCreateClient, formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, Copy, Check, ChevronRight, UserPlus } from "lucide-react";
import { toast } from "sonner";

const TIERS = ["Sprint", "Accelerator", "Fractional", "Membership/The Table", "Other"];
const STATUSES = ["active", "paused", "completed"];
const EMPTY = {
  name: "", email: "", company: "", engagement_label: "", tier: "Other",
  status: "active", grant_hub: true, grant_table: false,
};

const STATUS_STYLE = {
  active: "border-[#515E4F] text-[#515E4F]",
  paused: "border-[#A6633C] text-[#A6633C]",
  completed: "border-[rgba(28,27,25,0.3)] text-[#9A938A]",
};

export default function AdminClients() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      setItems(await adminGetClients(q));
    } catch (e) {
      // silent; admin route already guards
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      toast.error("Name, email, and company are required.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await adminCreateClient({ ...form, origin_url: window.location.origin });
      setResult(res);
      if (res.already_has_login) {
        toast.success("Client updated. They already have a login.");
      } else {
        toast.success("Client created and invite issued.");
      }
      setForm(EMPTY);
      load(search);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create client.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success("Invite link copied.");
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Clients</p>
          <h1 className="cv-display text-3xl mt-4">Invoice-based engagements.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-lg">
            Create and populate hub accounts for clients billed outside the app. No payment required.
          </p>
        </div>
        <button
          data-testid="admin-client-new-btn"
          onClick={() => { setShowForm((s) => !s); setResult(null); }}
          className="cv-btn-primary inline-flex items-center gap-2 whitespace-nowrap self-start"
        >
          <UserPlus size={15} /> New client
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form
          data-testid="admin-client-form"
          onSubmit={submit}
          className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] p-6 max-w-2xl space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name *">
              <input data-testid="client-name" className="cv-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Email *">
              <input data-testid="client-email" type="email" className="cv-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Company *">
              <input data-testid="client-company" className="cv-input" value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </Field>
            <Field label="Engagement label">
              <input data-testid="client-engagement" className="cv-input" placeholder="e.g. Fractional CTO"
                value={form.engagement_label}
                onChange={(e) => setForm({ ...form, engagement_label: e.target.value })} />
            </Field>
            <Field label="Tier">
              <select data-testid="client-tier" className="cv-input" value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="client-status" className="cv-input" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 pt-1">
            <Toggle label="Grant Hub access" testid="client-grant-hub"
              checked={form.grant_hub} onChange={(v) => setForm({ ...form, grant_hub: v })} />
            <Toggle label="Grant The Table" testid="client-grant-table"
              checked={form.grant_table} onChange={(v) => setForm({ ...form, grant_table: v })} />
          </div>
          <button data-testid="client-submit" disabled={busy}
            className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Plus size={14} /> {busy ? "Creating..." : "Create client"}
          </button>
        </form>
      )}

      {/* Result: invite link */}
      {result && (
        <div data-testid="admin-client-result"
          className="mt-6 border border-[rgba(28,27,25,0.22)] rounded-[16px] overflow-hidden max-w-2xl">
          <div className="cv-hairline-copper" />
          <div className="p-6">
            <p className="cv-kicker">{result.already_has_login ? "Existing account" : "Invite ready"}</p>
            <h3 className="cv-h2 text-base mt-2">{result.client?.name} · {result.client?.company}</h3>
            {result.welcome_link ? (
              <>
                <p className="cv-body text-sm text-[#1F1B16]/70 mt-3">
                  Send this magic link so the client can set a password and sign in.
                </p>
                <div className="mt-3 flex items-stretch gap-2">
                  <input readOnly data-testid="invite-link" value={result.welcome_link}
                    className="cv-input flex-1 text-xs" />
                  <button data-testid="copy-invite" onClick={() => copyLink(result.welcome_link)}
                    className="cv-btn-secondary inline-flex items-center gap-2 whitespace-nowrap">
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </>
            ) : (
              <p className="cv-body text-sm text-[#1F1B16]/70 mt-3">
                This person already has a Hub login. Their engagement was updated.
              </p>
            )}
            <DeliveryNote steps={result.steps} />
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mt-8 relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A938A]" />
        <input
          data-testid="admin-client-search"
          className="cv-input pl-9"
          placeholder="Search name, email, or company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* List */}
      <div className="mt-6">
        {loading ? (
          <p className="cv-body text-[#9A938A]">Loading...</p>
        ) : items.length === 0 ? (
          <p data-testid="admin-clients-empty" className="cv-body text-sm text-[#9A938A] border-l-2 border-[rgba(28,27,25,0.2)] pl-3">
            {search ? "No clients match your search." : "No clients yet. Create your first invoice-based client above."}
          </p>
        ) : (
          <div className="divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
            {items.map((c) => (
              <button
                key={c.id}
                data-testid="admin-client-row"
                onClick={() => navigate(`/admin/clients/${c.id}`)}
                className="w-full text-left py-4 grid grid-cols-12 gap-3 items-center group hover:bg-[rgba(28,27,25,0.02)] transition-colors"
              >
                <div className="col-span-12 sm:col-span-4">
                  <p className="cv-h2 text-sm">{c.name || "(no name)"}</p>
                  <p className="cv-body text-xs text-[#9A938A] mt-0.5">{c.email}</p>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <p className="cv-body text-sm text-[#1F1B16]/80">{c.company || "--"}</p>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <p className="cv-body text-xs text-[#1F1B16]/70">{c.engagement || "--"}</p>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <span className={`cv-sans uppercase tracking-[0.1em] text-[0.62rem] px-2.5 py-1 rounded-full border ${STATUS_STYLE[c.status] || STATUS_STYLE.completed}`}>
                    {c.status}
                  </span>
                  {!c.hub_access && (
                    <span className="cv-sans uppercase tracking-[0.1em] text-[0.58rem] text-[#A65A43] ml-2">revoked</span>
                  )}
                </div>
                <div className="col-span-6 sm:col-span-1 flex justify-end">
                  <ChevronRight size={16} className="text-[#9A938A] group-hover:text-[#A6633C] transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliveryNote({ steps }) {
  if (!steps) return null;
  const ac = steps.activecampaign;
  const zap = steps.zapier;
  const lines = [];
  if (ac) lines.push(ac.ok ? "ActiveCampaign: onboarding tag applied." : `ActiveCampaign skipped (${ac.error || "not configured"}).`);
  if (zap) lines.push(zap.ok ? (zap.skipped ? "Email notify: not configured (send link manually)." : "Email notify sent via Zapier.") : "Email notify failed (send link manually).");
  if (!lines.length) return null;
  return (
    <ul className="mt-4 space-y-1">
      {lines.map((l, i) => (
        <li key={i} className="cv-body text-xs text-[#1F1B16]/55 border-l-2 border-[rgba(28,27,25,0.15)] pl-3">{l}</li>
      ))}
    </ul>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2.5"
    >
      <span className={`w-9 h-5 rounded-full border transition-colors relative ${checked ? "bg-[#A6633C] border-[#A6633C]" : "bg-transparent border-[rgba(28,27,25,0.3)]"}`}>
        <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${checked ? "left-4 bg-[#F6F1E9]" : "left-0.5 bg-[#9A938A]"}`} />
      </span>
      <span className="cv-sans uppercase tracking-[0.1em] text-[0.68rem] text-[#1F1B16]/70">{label}</span>
    </button>
  );
}
